import { useEffect, useState } from "react";
import { httpDelete, httpPost } from "../../../modules/Backend";
import { TableVirtuoso } from "react-virtuoso";
import { Button, TabPanel } from "react-aria-components";
import iconPlus from "../../../assets/icons/icon-plus-white.svg";
import iconEdit from "../../../assets/icons/icon-pen-white.svg";
import iconDelete from "../../../assets/icons/icon-bin-red.svg";
import iconTick from "../../../assets/icons/icon-tick-blue.svg";
import { useNavigate } from "react-router";
import * as Navigation from "../../../modules/Navigation";
import { modalSignal } from "../Modal/ModalContainer";
import ConfirmModal, { ConfirmType } from "../Modal/ConfirmModal";
import { HttpStatusCode } from "axios";
import { IFilter } from "../../../hooks/UseFilters";
import { PermissionContextType, usePermissions } from "../../../modules/auth/Permissions/PermissionContext";
import { hasPermission } from "../../../modules/auth/Permissions/PermissionModule";
import { PermissionConcerns, PermissionTypes } from "../../../modules/auth/Permissions/PermissionStrings";
import { IAddress } from "../entityComponents/Form/components/FormAddress";
import { formatAddressParts } from "../../../helpers/StringHelper";

export interface IAddressBook {
    entityName: string,
    parentId: string | undefined,
    i: any,
    filters: IFilter[],
    canAdd?: boolean | ((ctx: PermissionContextType, accountId: string | undefined) => boolean)
}

export default function AddressBook({ entityName, i, filters, parentId, canAdd: canAddProp }: IAddressBook) {
    const [data, setData] = useState<any>(undefined);
    const [resultsKey] = useState<string>(entityName);
    const [showLoader, setShowLoader] = useState<boolean>(false)
    const navigate = useNavigate();
    const permissionsContext = usePermissions();

    async function getData() {
        const timeout = setTimeout(() => setShowLoader(true), 200)
        const payload = { filters };
        const result = await httpPost(`api/${entityName}/list`, payload);
        if (result?.data) {
            setData(result?.data);
        }
        setShowLoader(false);
        clearTimeout(timeout);
    }

    async function deleteEntity(id: string) {
        const payload = { id }
        const result = await httpDelete(`api/${entityName}/delete`, payload)
        if (result?.status === HttpStatusCode.Ok) {
            setData({ [resultsKey]: data[resultsKey].filter((x: any) => x.id != id) })
        }
    }

    function handleAddNew() {
        const navPayload = { parentId }
        Navigation.create.navigate(navigate, entityName, navPayload)
    }

    function handleEdit(id: string) {
        Navigation.edit.navigate(navigate, entityName, id)
    }

    function handleDeleteButtonPress(id: string) {
        modalSignal.value = <ConfirmModal heading={`Delete address?`} information="This action can not be undone" ContinueFunc={() => deleteEntity(id)} type={ConfirmType.Destructive} />
    }

    useEffect(() => {
        getData()
    }, [])

    function canAdd(): boolean {
        if (canAddProp !== undefined) {
            return typeof canAddProp === 'function' ? canAddProp(permissionsContext!, parentId) : canAddProp;
        }
        // Default to true for backward compatibility if canAdd is not specified
        return true;
    }

    function canEdit(): boolean {
        return hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Account_Address_Book);
    }

    function canDelete(): boolean {
        return hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Account_Address_Book);
    }

    return (<>
        <TabPanel id={i} className={"flex__grow--0"}>
            <div className="display--flex justifyContent--space-between margin__bottom--s">
                <div>
                </div>
                {canAdd() &&
                    <Button className="button button--small button--primary button--with-icon" onPress={handleAddNew}>
                        <img src={iconPlus} />
                        <p>Add address</p>
                    </Button>
                }
            </div>
        </TabPanel>

        <TabPanel id={i}>
            <div className="list__wrapper">
                <div className="list" data-testid="list-wrapper">
                    {!data && renderLoadingList()}
                    {data && data[resultsKey] !== undefined && data[resultsKey].length > 0 && renderListTable()}
                </div>
            </div>
        </TabPanel>
    </>
    )

    function displayAddressParts(address: IAddress) {
        return <div className="addressBook__address-parts">
            {formatAddressParts(address).map(x => <p>{x}</p>)}
        </div>
    }

    function renderListTable() {
        return (
            <>
                <TableVirtuoso
                    className="react-aria-Table react-aria-TableNoPadding"
                    style={{}}
                    data={data[resultsKey]}
                    initialItemCount={data[resultsKey].length}
                    itemContent={(index, item) => (
                        <>
                            <td className="addressBook__address-content-wrapper">
                                <div className="addressBook__address-header">
                                    <p className="addressBook__address-title">{item.title}</p>
                                    {item.isDefault && renderDefaultPill()}
                                </div>
                                <hr className="margin__top--xs margin__bottom--xs" />
                                {displayAddressParts(item.address)}
                            </td>
                            <td className="addressBook__right-content-td">
                                <div className="addressBook__right-content-wrapper">
                                    <div className="addressBook__right-content">
                                        {canEdit() &&
                                            <Button className="addressBook__address-update button button--small button--primary button--with-icon" onPress={() => handleEdit(item.id)}>
                                                <img src={iconEdit} />
                                                <p className="margin__right--xxs">Edit</p>
                                            </Button>
                                        }
                                        {canDelete() && item.canBeDeleted &&
                                            <Button className="addressBook__address-delete button button--small button--secondary button--secondary--destructive button--with-icon" onPress={() => handleDeleteButtonPress(item.id)}>
                                                <img src={iconDelete} />
                                                <p>Delete</p>
                                            </Button>
                                        }
                                    </div>
                                </div>
                            </td>
                        </>
                    )}
                />
            </>
        )
    }

    function renderDefaultPill() {
        return <div className="addressBook__address-default-pill">
            <img src={iconTick} />
            <p>Default</p>
        </div>
    }

    function renderLoadingList() {
        return (
            <div>
                <div className="react-aria-Table">
                    <table >
                        {showLoader && [...Array(7)].map((e, i) => (
                            <tr>
                                <td>
                                    <div className="loaders loaders__list loaders__list--full-width"></div>
                                </td>
                            </tr>
                        ))}
                    </table>
                </div>
            </div>
        )
    }
}